import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/core/auth/auth';

export type OnboardingStatus = 'not_started' | 'in_progress' | 'complete';
export type AccountStatus = 'unverified' | 'onboarding_required' | 'pending_approval' | 'trial' | 'active' | 'expired' | 'suspended' | 'rejected' | 'pending';

export interface OnboardingState {
  // Status
  status: OnboardingStatus;
  accountStatus: AccountStatus | null;
  
  // Step completion
  identityCompleted: boolean;
  roleCompleted: boolean;
  contactCompleted: boolean;
  preferencesCompleted: boolean;
  
  // Identity fields
  legalName: string | null;
  professionalCategory: string | null;
  countryOrRegion: string | null;
  
  // Role fields
  primaryRole: string | null;
  intendedUse: string | null;
  
  // Approval fields
  approvedAt: string | null;
  approvalDecision: string | null;
  grantedCapabilities: string[];
  
  // Computed
  canAccessClinical: boolean;
  isPendingApproval: boolean;
  needsOnboarding: boolean;
}

const DEFAULT_STATE: OnboardingState = {
  status: 'not_started',
  accountStatus: null,
  identityCompleted: false,
  roleCompleted: false,
  contactCompleted: false,
  preferencesCompleted: false,
  legalName: null,
  professionalCategory: null,
  countryOrRegion: null,
  primaryRole: null,
  intendedUse: null,
  approvedAt: null,
  approvalDecision: null,
  grantedCapabilities: [],
  canAccessClinical: false,
  isPendingApproval: false,
  needsOnboarding: true,
};

export function useOnboardingStatus() {
  const { user, loading: authLoading } = useAuth();
  const [state, setState] = useState<OnboardingState>(DEFAULT_STATE);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchOnboardingStatus = useCallback(async () => {
    if (!user?.id) {
      setState(DEFAULT_STATE);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Fetch onboarding record
      const { data: onboarding, error: onboardingError } = await supabase
        .from('user_onboarding')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (onboardingError) {
        // If no record exists, user needs onboarding
        if (onboardingError.code === 'PGRST116') {
          setState({
            ...DEFAULT_STATE,
            needsOnboarding: true,
          });
          setLoading(false);
          return;
        }
        throw onboardingError;
      }

      // Fetch account status
      const { data: account, error: accountError } = await supabase
        .from('accounts')
        .select('status')
        .eq('user_id', user.id)
        .single();

      if (accountError && accountError.code !== 'PGRST116') {
        console.warn('[useOnboardingStatus] Account fetch error:', accountError);
      }

      const accountStatus = (account?.status as AccountStatus) || null;
      const onboardingStatus = (onboarding.status as OnboardingStatus) || 'not_started';

      setState({
        status: onboardingStatus,
        accountStatus,
        identityCompleted: !!onboarding.identity_completed_at,
        roleCompleted: !!onboarding.role_completed_at,
        contactCompleted: !!onboarding.contact_completed_at,
        preferencesCompleted: !!onboarding.preferences_completed_at,
        legalName: onboarding.legal_name,
        professionalCategory: onboarding.professional_category,
        countryOrRegion: onboarding.country_or_region,
        primaryRole: onboarding.primary_role,
        intendedUse: onboarding.intended_use,
        approvedAt: onboarding.approved_at,
        approvalDecision: onboarding.approval_decision,
        grantedCapabilities: onboarding.granted_capabilities || [],
        canAccessClinical: accountStatus === 'active' && onboardingStatus === 'complete',
        isPendingApproval: accountStatus === 'pending_approval',
        needsOnboarding: onboardingStatus !== 'complete',
      });
    } catch (err) {
      console.error('[useOnboardingStatus] Error:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch onboarding status');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (!authLoading) {
      fetchOnboardingStatus();
    }
  }, [authLoading, fetchOnboardingStatus]);

  return {
    ...state,
    loading: authLoading || loading,
    error,
    refetch: fetchOnboardingStatus,
  };
}
